import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, Scissors, Palette, Crown, Wind, Brush,
  Hand, Heart, Eye, Smile, ArrowRight
} from "lucide-react";
import Reveal from "@/components/Reveal";
import TiltCard from "@/components/TiltCard";
import { SOBRE_HERO } from "@/data/gallery";

const cabeleireiro = [
  {
    icon: Scissors,
    title: "Corte",
    description: "Cortes personalizados ao seu rosto e estilo de vida.",
    items: [
      { name: "Corte Cabelo", price: "17,00 €" },
    ],
  },
  {
    icon: Wind,
    title: "Brushing",
    description: "Modelagem profissional para um acabamento impecável.",
    items: [
      { name: "Curto", price: "10,50 €" },
      { name: "Médio", price: "12,50 €" },
      { name: "Comprido", price: "15,50 €" },
    ],
  },
  {
    icon: Palette,
    title: "Coloração",
    description: "Cor de raiz, global ou sem amoníaco — sempre cuidada.",
    items: [
      { name: "Crescimentos", price: "29,00 €" },
      { name: "Global", price: "32,00 €" },
      { name: "Sem amoníaco · Crescimentos", price: "30,50 €" },
      { name: "Sem amoníaco · Global", price: "35,00 €" },
      { name: "Matização", price: "26,50 €" },
    ],
  },
  {
    icon: Sparkles,
    title: "Balayage & Madeixas",
    description: "Iluminação artesanal — a especialidade da casa.",
    featured: true,
    items: [
      { name: "Madeixas / Nuances", price: "60,00 € — 130,00 €" },
      { name: "Balayage", price: "100,00 € — 200,00 €", highlight: true },
      { name: "Morena Iluminada", price: "100,00 € — 200,00 €", highlight: true },
    ],
  },
  {
    icon: Crown,
    title: "Tratamentos",
    description: "Botox, alisamento e rituais de spa capilar.",
    items: [
      { name: "Botox Capilar", price: "50,00 € — 80,00 €" },
      { name: "Alisamento Progressivo", price: "50,00 € — 170,00 €" },
      { name: "Spa Capilar", price: "30,00 € — 70,00 €" },
    ],
  },
  {
    icon: Brush,
    title: "Penteados",
    description: "Penteados elegantes para o seu dia especial.",
    items: [
      { name: "Penteado simples", price: "35,00 € — 80,00 €" },
      { name: "Noiva / cerimónia", price: "80,00 € — 180,00 €" },
    ],
  },
];

const estetica = [
  {
    icon: Hand,
    title: "Unhas de Gel",
    description: "Unhas perfeitas, com ou sem extensão.",
    items: [
      { name: "Com extensão", price: "30,00 €" },
      { name: "Sem extensão", price: "27,00 €" },
      { name: "Manutenção", price: "25,00 €" },
      { name: "Gelinho", price: "18,00 €" },
      { name: "Nail Art / Francesa", price: "5,00 €" },
    ],
  },
  {
    icon: Heart,
    title: "Manicure & Pedicure",
    description: "Cuidado completo para mãos e pés.",
    items: [
      { name: "Manicure", price: "10,00 €" },
      { name: "Pedicure", price: "20,00 €" },
      { name: "Pedicure com gelinho", price: "25,00 €" },
    ],
  },
  {
    icon: Smile,
    title: "Maquilhagem",
    description: "Para o dia ou para a noite mais especial.",
    items: [
      { name: "Maquilhagem dia", price: "30,00 €" },
      { name: "Maquilhagem noite / evento", price: "50,00 €" },
    ],
  },
  {
    icon: Eye,
    title: "Depilação",
    description: "Limpeza facial precisa e cuidada.",
    items: [
      { name: "Buço", price: "3,50 €" },
      { name: "Queixo", price: "3,50 €" },
      { name: "Sobrancelhas", price: "5,50 €" },
      { name: "Facial completo", price: "7,00 €" },
    ],
  },
];

const ServiceCard = ({ service, idx }) => {
  const Icon = service.icon;
  return (
    <TiltCard
      maxTilt={5}
      className={`relative p-8 lg:p-10 group ${
        service.featured
          ? "bg-gradient-to-br from-beige-light to-beige border border-gold shadow-[0_20px_60px_-15px_rgba(218,165,32,0.35)]"
          : "bg-white border border-ink/8 hover:border-gold/40"
      } transition-all duration-500`}
    >
      <article data-testid={`service-card-${service.title.toLowerCase().replace(/\s|&/g, "-")}`}>
        {service.featured && (
          <span className="absolute -top-3 left-8 bg-gold-bright text-ink px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-medium shadow-[0_0_20px_rgba(255,215,0,0.5)]">
            ★ Especialidade
          </span>
        )}

        <div className="flex items-start justify-between mb-6">
          <div className={`w-14 h-14 flex items-center justify-center transition-all duration-500 ${
            service.featured
              ? "bg-gold-bright shadow-[0_0_25px_rgba(255,215,0,0.5)]"
              : "bg-beige border border-gold/30 group-hover:bg-gold group-hover:border-gold"
          }`}>
            <Icon
              className={`w-6 h-6 transition-colors ${
                service.featured ? "text-ink" : "text-gold group-hover:text-ink"
              }`}
              strokeWidth={1.5}
            />
          </div>
          <span className="overline opacity-50">0{idx + 1}</span>
        </div>

        <h3 className="font-serif text-3xl text-ink leading-tight">{service.title}</h3>
        <p className="text-sm text-ink/65 mt-3 leading-relaxed mb-6">{service.description}</p>

        <ul className="space-y-3 border-t border-ink/10 pt-5">
          {service.items.map((item) => (
            <li
              key={item.name}
              className="flex items-baseline justify-between gap-3"
            >
              <span
                className={`text-sm ${
                  item.highlight ? "text-ink font-medium" : "text-ink/80"
                }`}
              >
                {item.name}
              </span>
              <span
                className={`tabular-nums text-sm font-medium whitespace-nowrap ${
                  item.highlight
                    ? "text-gold-dark"
                    : "text-ink"
                }`}
              >
                {item.price}
              </span>
            </li>
          ))}
        </ul>
      </article>
    </TiltCard>
  );
};

const Servicos = () => {
  const [tab, setTab] = useState("cabeleireiro");
  const list = tab === "cabeleireiro" ? cabeleireiro : estetica;

  return (
    <div data-testid="servicos-page" className="pb-24 overflow-x-clip">
      <section className="container-luxe py-12">
        <Reveal>
          <p className="overline mb-4">— Preçário</p>
          <h1 className="font-serif text-6xl sm:text-7xl lg:text-8xl font-light leading-[0.95] text-ink max-w-3xl">
            Serviços e <span className="italic gold-text">preços</span>
          </h1>
          <p className="mt-6 text-ink/70 max-w-xl text-lg leading-relaxed">
            Valores indicativos. Tratamentos personalizados podem variar conforme o
            comprimento e densidade do cabelo. <span className="text-gold-dark font-medium">Aconselhamento gratuito.</span>
          </p>
        </Reveal>
      </section>

      {/* TABS — large, clear, visual */}
      <section className="container-luxe">
        <div className="grid sm:grid-cols-2 gap-4 mb-12 max-w-2xl" data-testid="servicos-tabs">
          <button
            onClick={() => setTab("cabeleireiro")}
            data-testid="tab-cabeleireiro"
            className={`group relative p-6 text-left transition-all duration-500 overflow-hidden ${
              tab === "cabeleireiro"
                ? "bg-ink text-beige shadow-[0_20px_50px_-10px_rgba(44,44,44,0.4)]"
                : "bg-white text-ink border border-ink/10 hover:border-gold"
            }`}
          >
            <Scissors className={`w-6 h-6 mb-3 ${tab === "cabeleireiro" ? "text-gold" : "text-gold"}`} strokeWidth={1.5} />
            <p className={`overline ${tab === "cabeleireiro" ? "!text-gold" : ""}`}>01 · Cabelo</p>
            <p className="font-serif text-2xl mt-1">Cabeleireiro</p>
            {tab === "cabeleireiro" && (
              <span className="absolute top-4 right-4 w-2 h-2 bg-gold-bright rounded-full shadow-[0_0_15px_rgba(255,215,0,0.8)]" />
            )}
          </button>

          <button
            onClick={() => setTab("estetica")}
            data-testid="tab-estetica"
            className={`group relative p-6 text-left transition-all duration-500 overflow-hidden ${
              tab === "estetica"
                ? "bg-ink text-beige shadow-[0_20px_50px_-10px_rgba(44,44,44,0.4)]"
                : "bg-white text-ink border border-ink/10 hover:border-gold"
            }`}
          >
            <Sparkles className={`w-6 h-6 mb-3 text-gold`} strokeWidth={1.5} />
            <p className={`overline ${tab === "estetica" ? "!text-gold" : ""}`}>02 · Beleza</p>
            <p className="font-serif text-2xl mt-1">Estética</p>
            {tab === "estetica" && (
              <span className="absolute top-4 right-4 w-2 h-2 bg-gold-bright rounded-full shadow-[0_0_15px_rgba(255,215,0,0.8)]" />
            )}
          </button>
        </div>
      </section>

      {/* SERVICE CARDS GRID */}
      <section className="container-luxe">
        <Reveal stagger className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {list.map((s, idx) => (
            <ServiceCard key={s.title} service={s} idx={idx} />
          ))}
        </Reveal>
      </section>

      {/* CTA */}
      <section className="container-luxe mt-24">
        <Reveal>
          <div className="bg-ink text-beige relative overflow-hidden grid lg:grid-cols-12">
            <div className="lg:col-span-5 relative min-h-[280px] lg:min-h-[380px]">
              <img
                src={SOBRE_HERO}
                alt="Tamara Alves"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-ink lg:to-ink/40" />
            </div>
            <div className="lg:col-span-7 p-10 lg:p-14 relative flex flex-col justify-center">
              <span aria-hidden className="absolute -top-20 -right-20 font-serif italic text-[16rem] leading-none text-gold/[0.08] select-none pointer-events-none">A</span>
              <p className="overline !text-gold mb-3 relative">— Aconselhamento</p>
              <h3 className="font-serif text-4xl lg:text-5xl font-light leading-tight relative">
                Não sabe que serviço escolher? <br />
                <span className="italic gold-text">Falamos consigo.</span>
              </h3>
              <p className="mt-4 text-beige/70 max-w-2xl relative">
                Antes de qualquer técnica de coloração ou tratamento, ofereço uma consulta gratuita
                para perceber o seu cabelo e desenhar o melhor caminho.
              </p>
              <div className="mt-8 relative">
                <Link to="/agendamentos" data-testid="servicos-cta-btn" className="btn-gold inline-flex">
                  Pedir consulta <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Servicos;
